import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Mounts `children` only once this spot is about to scroll into view — and with them, the queries
 * they make. For the parts of a page below the fold: on a phone most of Home is, and every card
 * that fetched up front made the first screen wait behind requests for things nobody had seen.
 *
 * `rootMargin` starts a little before the spot is on screen, so a card is usually loaded by the
 * time it is looked at. Once shown it stays shown. Where IntersectionObserver does not exist (old
 * WebViews, the test DOM) it renders straight away, exactly as before.
 */
export function WhenVisible({
  children,
  placeholder = null,
  rootMargin = '300px',
}: {
  children: ReactNode;
  /** Holds the space until then, so the page does not jump. */
  placeholder?: ReactNode;
  rootMargin?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(() => typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    if (visible) return;
    const element = ref.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [visible, rootMargin]);

  if (visible) return <>{children}</>;
  return <div ref={ref}>{placeholder}</div>;
}
